import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, UserCog, Shield, MessageSquare, Ban, X, Save, Trash2, UserPlus, Mail, Copy, Key } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsersTab({ users, token, onRefresh, currentUserEmail }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Invite user state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    full_name: '',
    role: 'editor',
    send_invite_email: true
  });
  const [isInviting, setIsInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  // Password reset state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetSendEmail, setResetSendEmail] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.fullName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // Select all except current user
      const selectableIds = filteredUsers
        .filter(u => u.email !== currentUserEmail)
        .map(u => u.id);
      setSelectedUsers(selectableIds);
    } else {
      setSelectedUsers([]);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteData.email || !inviteData.full_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsInviting(true);
    try {
      const res = await axios.post(`${API}/admin/users/invite`, inviteData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setInviteResult(res.data);
      toast.success(res.data.message);
      
      if (!res.data.email_sent && res.data.temp_password) {
        // Show the temp password if email wasn't sent
        toast.info('Email not sent - copy the temporary password', { duration: 10000 });
      }
      
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite user');
    } finally {
      setIsInviting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const resetInviteModal = () => {
    setShowInviteModal(false);
    setInviteData({ email: '', full_name: '', role: 'editor', send_invite_email: true });
    setInviteResult(null);
  };

  const handleResetPassword = async () => {
    if (!resetUser) return;
    
    setIsResetting(true);
    try {
      const res = await axios.post(`${API}/admin/users/${resetUser.id}/reset-password`, {
        send_email: resetSendEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResetResult(res.data);
      toast.success(`Password reset for ${resetUser.email}`);
      
      if (!res.data.email_sent && res.data.temp_password) {
        toast.info('Email not sent - copy the new password', { duration: 10000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsResetting(false);
    }
  };

  const openResetModal = (user) => {
    setResetUser(user);
    setResetSendEmail(true);
    setResetResult(null);
    setShowResetModal(true);
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetUser(null);
    setResetResult(null);
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.email}? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/admin/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User ${user.email} deleted`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} users? This cannot be undone.`)) return;
    
    setIsDeleting(true);
    try {
      const res = await axios.post(`${API}/admin/users/bulk-delete`, {
        user_ids: selectedUsers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setSelectedUsers([]);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete users');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setUserPermissions({
      role: u.role || 'user',
      isMuted: u.permissions?.isMuted || false,
      canPost: u.permissions?.canPost !== false,
      canSendDMs: u.permissions?.canSendDMs !== false,
      canAccessForum: u.permissions?.canAccessForum !== false,
      isBanned: u.permissions?.isBanned || false,
      banReason: u.permissions?.banReason || ''
    });
  };

  const handleSaveUserPermissions = async () => {
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}/permissions`, userPermissions, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User permissions updated');
      setEditingUser(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update permissions');
    }
  };

  return (
    <>
      <Card data-testid="admin-users-tab">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-4">
            <CardTitle>All Users</CardTitle>
            {selectedUsers.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={isDeleting}
                data-testid="bulk-delete-users-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete {selectedUsers.length} Selected
              </Button>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setShowInviteModal(true)}
              className="bg-secondary hover:bg-secondary/90"
              data-testid="invite-user-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Invite User
            </Button>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium text-muted-foreground w-10">
                    <Checkbox
                      checked={selectedUsers.length > 0 && selectedUsers.length === filteredUsers.filter(u => u.email !== currentUserEmail).length}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-users"
                    />
                  </th>
                  <th className="text-left p-3 font-medium text-muted-foreground">User</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Role</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Joined</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className={`border-t ${selectedUsers.includes(u.id) ? 'bg-red-50' : ''}`}>
                    <td className="p-3">
                      {u.email !== currentUserEmail && (
                        <Checkbox
                          checked={selectedUsers.includes(u.id)}
                          onCheckedChange={(checked) => handleSelectUser(u.id, checked)}
                          data-testid={`select-user-${u.id}`}
                        />
                      )}
                    </td>
                    <td className="p-3">
                      <div>
                        <p className="font-medium">{u.fullName || 'No name'}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant={
                        u.role === 'admin' ? 'default' : 
                        u.role === 'moderator' ? 'secondary' :
                        u.role === 'editor' ? 'outline' : 'outline'
                      } className={
                        u.role === 'admin' ? 'bg-primary' : 
                        u.role === 'moderator' ? 'bg-blue-600 text-white' :
                        u.role === 'editor' ? 'bg-purple-100 text-purple-800' : ''
                      }>
                        {u.role || 'user'}
                      </Badge>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-3">
                      {u.permissions?.isBanned ? (
                        <Badge className="bg-red-100 text-red-800">Banned</Badge>
                      ) : u.permissions?.isMuted ? (
                        <Badge className="bg-yellow-100 text-yellow-800">Muted</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditUser(u)}
                          title="Edit Permissions"
                        >
                          <UserCog className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openResetModal(u)}
                          title="Reset Password"
                          className="text-amber-600 hover:text-amber-700"
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        {u.email !== currentUserEmail && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteUser(u)}
                            title="Delete User"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Permissions Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Edit User Permissions</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setEditingUser(null)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-medium">{editingUser.fullName || 'No name'}</p>
                <p className="text-sm text-muted-foreground">{editingUser.email}</p>
              </div>

              <div>
                <Label>Role</Label>
                <Select
                  value={userPermissions.role}
                  onValueChange={(v) => setUserPermissions({ ...userPermissions, role: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="moderator">Moderator</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {userPermissions.role === 'admin' && 'Full access to all features'}
                  {userPermissions.role === 'moderator' && 'Manage trips, bookings, promos, waitlist, reminders'}
                  {userPermissions.role === 'editor' && 'Edit trip content only'}
                  {userPermissions.role === 'user' && 'Regular member access'}
                </p>
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Can Post in Forum</span>
                  </div>
                  <Switch
                    checked={userPermissions.canPost}
                    onCheckedChange={(v) => setUserPermissions({ ...userPermissions, canPost: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Can Send DMs</span>
                  </div>
                  <Switch
                    checked={userPermissions.canSendDMs}
                    onCheckedChange={(v) => setUserPermissions({ ...userPermissions, canSendDMs: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Ban className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Muted</span>
                  </div>
                  <Switch
                    checked={userPermissions.isMuted}
                    onCheckedChange={(v) => setUserPermissions({ ...userPermissions, isMuted: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Ban className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600">Banned</span>
                  </div>
                  <Switch
                    checked={userPermissions.isBanned}
                    onCheckedChange={(v) => setUserPermissions({ ...userPermissions, isBanned: v })}
                  />
                </div>
                {userPermissions.isBanned && (
                  <div>
                    <Label className="text-sm">Ban Reason</Label>
                    <Input
                      placeholder="Reason for ban..."
                      value={userPermissions.banReason}
                      onChange={(e) => setUserPermissions({ ...userPermissions, banReason: e.target.value })}
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditingUser(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveUserPermissions}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Permissions
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Invite User
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={resetInviteModal}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {!inviteResult ? (
                <form onSubmit={handleInviteUser} className="space-y-4">
                  <div>
                    <Label htmlFor="invite-email">Email *</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="user@example.com"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="invite-name">Full Name *</Label>
                    <Input
                      id="invite-name"
                      type="text"
                      placeholder="John Doe"
                      value={inviteData.full_name}
                      onChange={(e) => setInviteData({ ...inviteData, full_name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Role</Label>
                    <Select
                      value={inviteData.role}
                      onValueChange={(v) => setInviteData({ ...inviteData, role: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="editor">Editor - Can edit trips</SelectItem>
                        <SelectItem value="moderator">Moderator - Can manage bookings</SelectItem>
                        <SelectItem value="admin">Admin - Full access</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Send invite email</span>
                    </div>
                    <Switch
                      checked={inviteData.send_invite_email}
                      onCheckedChange={(v) => setInviteData({ ...inviteData, send_invite_email: v })}
                    />
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    {inviteData.send_invite_email 
                      ? 'User will receive an email with login credentials'
                      : 'You will need to share the temporary password manually'}
                  </p>
                  
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={resetInviteModal}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isInviting}>
                      {isInviting ? 'Inviting...' : 'Send Invite'}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <UserPlus className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="font-semibold text-lg">User Invited Successfully!</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {inviteResult.user.email} has been added as {inviteResult.user.role}
                    </p>
                  </div>
                  
                  {inviteResult.email_sent ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800">
                        <Mail className="h-4 w-4 inline mr-1" />
                        Invite email sent to {inviteResult.user.email}
                      </p>
                    </div>
                  ) : inviteResult.temp_password && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                      <p className="text-sm text-amber-800 font-medium">
                        Email not configured. Share these credentials manually:
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between bg-white rounded p-2">
                          <div>
                            <p className="text-xs text-muted-foreground">Email</p>
                            <p className="text-sm font-mono">{inviteResult.user.email}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => copyToClipboard(inviteResult.user.email)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between bg-white rounded p-2">
                          <div>
                            <p className="text-xs text-muted-foreground">Temporary Password</p>
                            <p className="text-sm font-mono">{inviteResult.temp_password}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => copyToClipboard(inviteResult.temp_password)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end pt-2">
                    <Button onClick={resetInviteModal}>
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Password Reset Modal */}
      {showResetModal && resetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Reset Password
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={closeResetModal}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {!resetResult ? (
                <div className="space-y-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Reset password for:</p>
                    <p className="font-medium">{resetUser.full_name || resetUser.fullName || 'No name'}</p>
                    <p className="text-sm text-muted-foreground">{resetUser.email}</p>
                  </div>
                  
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Send password reset email</span>
                    </div>
                    <Switch
                      checked={resetSendEmail}
                      onCheckedChange={setResetSendEmail}
                    />
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    {resetSendEmail 
                      ? 'User will receive an email with new login credentials'
                      : 'You will need to share the new password manually'}
                  </p>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      ⚠️ This will generate a new temporary password and invalidate the current one.
                    </p>
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={closeResetModal}>
                      Cancel
                    </Button>
                    <Button onClick={handleResetPassword} disabled={isResetting}>
                      {isResetting ? 'Resetting...' : 'Reset Password'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Key className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="font-semibold text-lg">Password Reset Successfully!</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      New password generated for {resetResult.email}
                    </p>
                  </div>
                  
                  {resetResult.email_sent ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800">
                        <Mail className="h-4 w-4 inline mr-1" />
                        Password reset email sent to {resetResult.email}
                      </p>
                    </div>
                  ) : resetResult.temp_password && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                      <p className="text-sm text-amber-800 font-medium">
                        Email not sent. Share these credentials manually:
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between bg-white rounded p-2">
                          <div>
                            <p className="text-xs text-muted-foreground">Email</p>
                            <p className="text-sm font-mono">{resetResult.email}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => copyToClipboard(resetResult.email)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between bg-white rounded p-2">
                          <div>
                            <p className="text-xs text-muted-foreground">New Password</p>
                            <p className="text-sm font-mono">{resetResult.temp_password}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => copyToClipboard(resetResult.temp_password)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end pt-2">
                    <Button onClick={closeResetModal}>
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
